import { z } from 'zod';
import {
  ApiConfigSchema,
  ConversationPromptTokenBudgetSchema,
  type CustomPromptPostProcessingType,
  LLMMaxTokensSchema,
  type LLMMessage,
  LLMMinPSchema,
  LLMPenaltySchema,
  LLMProviderTypeSchema,
  LLMRepetitionPenaltySchema,
  LLMTemperatureSchema,
  LLMTopASchema,
  LLMTopKSchema,
  LLMTopPSchema,
  MemorySettingsSchema,
  RegexConfigSchema,
  RegexRuleSchema,
} from './base';
import { createGardenSTPresetDefaults } from './defaults';
import { STPresetConfigSchema, STPresetSchema } from './presets';
import { type WorldBook, WorldBookConfigSchema, WorldBookSchema } from './worldBook';

export const LLMRuntimeModelProfileSchema = z.enum(['primary', 'lightweight']);
export type LLMRuntimeModelProfile = z.infer<typeof LLMRuntimeModelProfileSchema>;

export interface LLMGenerateOptions {
  /** 调用链追踪 ID（可选，不传则由 Provider 生成） */
  request_id?: string;
  user_input?: string;
  messages?: LLMMessage[];
  /** 预设 marker 节点的附加载荷，编译阶段按 identifier 注入。 */
  marker_payloads?: Record<string, string[]>;
  /** 显式主提示覆盖。 */
  main_prompt_override?: string;
  /** 提示词宏上下文（如 user/char） */
  macro_context?: Record<string, string>;
  /** 世界书匹配上下文（角色名/标签） */
  character_context?: {
    names?: string[];
    tags?: string[];
  };
  generation_type?: string;
  apply_st_preset?: boolean;
  use_sysprompt?: boolean;
  custom_prompt_post_processing?: CustomPromptPostProcessingType;
  world_book?: WorldBook;
  max_tokens?: number;
  temperature?: number | null;
  top_p?: number | null;
  top_k?: number | null;
  top_a?: number | null;
  min_p?: number | null;
  repetition_penalty?: number | null;
  presence_penalty?: number | null;
  frequency_penalty?: number | null;
  should_stream?: boolean;
  /** 请求时使用的模型分区，默认主模型，Ivy 等路由层可切到轻量模型。 */
  runtimeModelProfile?: LLMRuntimeModelProfile;
  /** 可选的请求中断信号，用于用户主动停止生成 */
  signal?: AbortSignal;
}

export interface LLMStreamHandlers {
  onChunk?: (chunkText: string, accumulatedText: string) => void;
  onDone?: (finalText: string) => void;
  onError?: (error: unknown) => void;
}

export const LLMConfigSchema = z.object({
  version: z.string().default('1.0.0'),
  provider: LLMProviderTypeSchema.default('openai-compatible'),
  baseUrl: z.string().min(1).default('https://api.openai.com/v1'),
  model: z.string().min(1).default('gpt-4.1-mini'),
  apiKey: z.string().default(''),
  lightweightBaseUrl: z.string().default(''),
  lightweightModel: z.string().default(''),
  lightweightApiKey: z.string().default(''),
  embeddingEnabled: z.boolean().default(true),
  embeddingBaseUrl: z.string().default(''),
  embeddingModel: z.string().default('text-embedding-3-small'),
  embeddingApiKey: z.string().default(''),
  temperature: LLMTemperatureSchema,
  topP: LLMTopPSchema,
  topK: LLMTopKSchema,
  topA: LLMTopASchema,
  minP: LLMMinPSchema,
  repetitionPenalty: LLMRepetitionPenaltySchema,
  maxTokens: LLMMaxTokensSchema,
  presencePenalty: LLMPenaltySchema,
  frequencyPenalty: LLMPenaltySchema,
  apiConfigs: z.array(ApiConfigSchema).default([]),
  activeApiConfigId: z.string().default(''),
  stPreset: STPresetSchema.default(createGardenSTPresetDefaults()),
  worldBook: WorldBookSchema.default({
    enabled: false,
    global_settings: {
      scan_depth: 2,
      recursive_scan: true,
      max_recursion_rounds: null,
    },
    entries: [],
  }),
  presetConfigs: z.array(STPresetConfigSchema).default([]),
  activePresetConfigId: z.string().default(''),
  regexEnabled: z.boolean().default(true),
  regexGlobalRules: z.array(RegexRuleSchema).default([]),
  regexConfigs: z.array(RegexConfigSchema).default([]),
  activeRegexConfigId: z.string().default(''),
  worldBookConfigs: z.array(WorldBookConfigSchema).default([]),
  activeWorldBookConfigId: z.string().default(''),
  /** 是否向兼容后端发送 ST 风格扩展采样参数 */
  sendExtendedSamplingParams: z.boolean().default(false),
  /** 叙事面板是否启用流式输出（统一使用 OpenAI 兼容接口） */
  narrativeStream: z.boolean().default(false),
  /** 人物会话局部 prompt 历史预算，仅影响局部对话历史压缩，不影响模型输出长度 */
  conversationPromptTokenBudget: ConversationPromptTokenBudgetSchema,
  /** 统一主历史、摘要与向量召回相关运行设置 */
  memorySettings: MemorySettingsSchema.default(() => MemorySettingsSchema.parse({})),
});
export type LLMConfig = z.infer<typeof LLMConfigSchema>;

/**
 * 创建默认 LLM 配置
 */
export function createDefaultLLMConfig(): LLMConfig {
  return LLMConfigSchema.parse({});
}

/**
 * 合并 LLM 配置（用于填充默认值并统一校验）
 */
export function mergeLLMConfig(partial: Partial<LLMConfig>, defaults: LLMConfig = createDefaultLLMConfig()): LLMConfig {
  return LLMConfigSchema.parse({
    ...defaults,
    ...partial,
  });
}
